import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import { useTranslation } from 'react-i18next';

import { useHelp } from '../context/HelpProvider';
import HelpContent, { LoomThumbnail, formatDuration } from './HelpContent';
import { anchorLabel, anchorOrderIndex, WELCOME_ANCHOR } from './helpAnchors';
import authService from '../services/AuthService';
import { isWelcomeDismissed, setWelcomeDismissed, pickWelcomeArticle } from '../utils/welcomeVideo';

const GENERAL_KEY = '__general__';

// Section titles reuse the sidebar's localized labels so Help Center headers
// match the navigation exactly, in both languages.
const SECTION_I18N = {
    'welcome.intro': 'help.sectionWelcome',
    'documents.overview': 'sideBar.documents',
    'members.overview': 'sideBar.memberList',
    'settings.overview': 'sideBar.settings',
    'conversations.history': 'sideBar.conversationHistory',
    'analytics.overview': 'sideBar.nanalytics',
    'chatbots.settings': 'sideBar.chatbotMenu',
    'agents.overview': 'sideBar.agents',
    'agent.requests': 'sideBar.agentRequest',
    'tickets.overview': 'sideBar.tickets',
};

const langLabel = (v) => {
    const l = String(v || '').toLowerCase();
    if (l === 'jp' || l.startsWith('ja')) return '日本語';
    if (l === 'en') return 'English';
    return v || '—';
};

const formatDate = (secs) => {
    const n = Number(secs) || 0;
    if (!n) return '—';
    try {
        return new Date(n * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
        return '—';
    }
};

function ArticleCard({ article, onOpen }) {
    return (
        <Paper
            elevation={0}
            onClick={() => onOpen(article)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(article);
                }
            }}
            sx={{
                p: 2,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '10px',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { borderColor: 'primary.main', boxShadow: '0 2px 12px rgba(40,123,214,0.15)' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }}
        >
            <Box sx={{ mb: 1 }}>
                <LoomThumbnail thumbnailUrl={article.thumbnailUrl} duration={article.duration} width="100%" height={128} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: 'primary.main' }}>
                <OndemandVideoIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Video
                </Typography>
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.98rem', color: 'text.primary', mb: 0.5 }}>
                {article.title || 'Untitled'}
            </Typography>
            {article.summary ? (
                <Typography
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.85rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {article.summary}
                </Typography>
            ) : null}
        </Paper>
    );
}

// One metadata field (icon + label + value) in the video detail popup.
function DetailField({ icon, label, children }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{ color: 'text.disabled', display: 'flex', mt: '2px', '& svg': { fontSize: 18 } }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: '0.66rem',
                        fontWeight: 700,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        mb: 0.4,
                    }}
                >
                    {label}
                </Typography>
                <Box sx={{ fontSize: '0.9rem', fontWeight: 500, color: 'text.primary', lineHeight: 1.45 }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
}

export default function HelpCenter() {
    const { t, i18n } = useTranslation();
    const { articles, loading, error } = useHelp();
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(null);

    // "Show welcome video at login" toggle — a reversible undo for the modal's
    // "Don't show this again" checkbox. Keyed to the same user + video id.
    const effectiveUser = useMemo(
        () => String(authService.getLoginAs() || authService.getCurrentUser() || '').trim(),
        []
    );
    const welcomeArticle = useMemo(() => {
        const list = (Array.isArray(articles) ? articles : []).filter((a) =>
            (Array.isArray(a.anchorKeys) ? a.anchorKeys : []).includes(WELCOME_ANCHOR)
        );
        return pickWelcomeArticle(list, i18n.language);
    }, [articles, i18n.language]);
    const welcomeVideoId = welcomeArticle?.loomVideoId || '';
    const [showAtLogin, setShowAtLogin] = useState(true);

    useEffect(() => {
        if (welcomeVideoId && effectiveUser) {
            setShowAtLogin(!isWelcomeDismissed(effectiveUser, welcomeVideoId));
        }
    }, [welcomeVideoId, effectiveUser]);

    const handleToggleShowAtLogin = (checked) => {
        setShowAtLogin(checked);
        // checked = show at login → NOT dismissed.
        setWelcomeDismissed(effectiveUser, welcomeVideoId, !checked);
    };

    // Localized section header for a group (reuses sidebar labels; General special-cased).
    const sectionTitle = (key) => {
        if (key === GENERAL_KEY) return t('help.generalGroup', 'General');
        const k = SECTION_I18N[key];
        return k ? t(k, anchorLabel(key)) : anchorLabel(key);
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return articles;
        return articles.filter((a) => {
            const pageLabels = (Array.isArray(a.anchorKeys) ? a.anchorKeys : []).map((k) => anchorLabel(k)).join(' ');
            const haystack = [a.title, a.summary, pageLabels]
                .map((v) => String(v ?? '').toLowerCase())
                .join(' ');
            return haystack.includes(q);
        });
    }, [articles, query]);

    // Group page-wise: one section per page (anchor). A video attached to
    // multiple pages appears under each. Videos with no page fall under General.
    const grouped = useMemo(() => {
        const map = new Map();
        const add = (key, label, article) => {
            if (!map.has(key)) map.set(key, { label, items: [] });
            map.get(key).items.push(article);
        };
        filtered.forEach((a) => {
            const anchors = (Array.isArray(a.anchorKeys) ? a.anchorKeys : []).filter(Boolean);
            if (anchors.length === 0) {
                add(GENERAL_KEY, t('help.generalGroup', 'General'), a);
            } else {
                anchors.forEach((key) => add(key, anchorLabel(key), a));
            }
        });
        const entries = Array.from(map.entries());
        entries.forEach(([, g]) =>
            g.items.sort((x, y) => (x.order || 0) - (y.order || 0) || String(x.title || '').localeCompare(String(y.title || '')))
        );
        // Welcome first, then sidebar order; General always last.
        entries.sort((a, b) => {
            if (a[0] === GENERAL_KEY) return 1;
            if (b[0] === GENERAL_KEY) return -1;
            const ai = anchorOrderIndex(a[0]);
            const bi = anchorOrderIndex(b[0]);
            if (ai !== bi) return ai - bi;
            return a[1].label.localeCompare(b[1].label);
        });
        return entries;
    }, [filtered, t]);

    const selectedPages = selected
        ? (Array.isArray(selected.anchorKeys) ? selected.anchorKeys : []).map((k) => sectionTitle(k))
        : [];

    return (
        <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 1100, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: 'primary.main', flexShrink: 0 }} />
                <Box>
                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {t('sideBar.helpCenter', 'Help Center')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {t('help.centerSubtitle', 'Guides, tutorials and videos to help you get the most out of NetBot.')}
                    </Typography>
                </Box>
            </Box>

            {/* Search */}
            <TextField
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('help.searchPlaceholder', 'Search help articles…')}
                fullWidth
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    ),
                }}
                sx={{ maxWidth: 560 }}
            />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={30} />
                </Box>
            ) : error ? (
                <Alert severity="error">{t('help.loadError', 'Failed to load help content.')}</Alert>
            ) : articles.length === 0 ? (
                <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '12px' }}>
                    <HelpOutlineIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
                    <Typography sx={{ color: 'text.secondary' }}>
                        {t('help.emptyLibrary', 'No help articles are available yet.')}
                    </Typography>
                </Paper>
            ) : filtered.length === 0 ? (
                <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '12px' }}>
                    <Typography sx={{ color: 'text.secondary', mb: 0.5 }}>
                        {t('help.noResults', 'No results for "{{query}}".', { query })}
                    </Typography>
                    <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>
                        {t('help.noResultsHint', 'Try a different keyword or browse the categories.')}
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {grouped.map(([key, group]) => (
                        <Accordion
                            key={key}
                            defaultExpanded
                            disableGutters
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '12px !important',
                                overflow: 'hidden',
                                '&:before': { display: 'none' },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    px: 2,
                                    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                                }}
                            >
                                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary' }}>
                                    {sectionTitle(key)}
                                </Typography>
                                <Chip label={group.items.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                                {key === WELCOME_ANCHOR && welcomeVideoId ? (
                                    <Box
                                        sx={{
                                            mb: 1.5,
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            borderRadius: '10px',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.default',
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'text.primary' }}>
                                                {t('help.welcomeShowAtLogin', 'Show welcome video at login')}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                {t('help.welcomeShowAtLoginHint', 'Turn this on to see the intro again the next time you log in.')}
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={showAtLogin}
                                            onChange={(e) => handleToggleShowAtLogin(e.target.checked)}
                                            inputProps={{ 'aria-label': t('help.welcomeShowAtLogin', 'Show welcome video at login') }}
                                        />
                                    </Box>
                                ) : null}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 1.5,
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                        },
                                    }}
                                >
                                    {group.items.map((a) => (
                                        <ArticleCard key={`${key}:${a.articleId}`} article={a} onOpen={setSelected} />
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Article detail popup */}
            <Dialog
                open={Boolean(selected)}
                onClose={() => setSelected(null)}
                fullWidth
                maxWidth="md"
                scroll="body"
            >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mr: 'auto' }}>
                        {selected?.title}
                    </Typography>
                    <IconButton aria-label={t('help.close', 'Close')} onClick={() => setSelected(null)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ py: 3 }}>
                    {selected ? (
                        <>
                            <HelpContent article={selected} />

                            <Box
                                sx={{
                                    mt: 3,
                                    borderRadius: '14px',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.default',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2.5,
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary' }}>
                                        {t('help.details', 'Details')}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        columnGap: 4,
                                        rowGap: 2.25,
                                    }}
                                >
                                    <DetailField icon={<WebOutlinedIcon />} label={t('help.fieldPage', 'Page')}>
                                        {selectedPages.length ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selectedPages.map((p) => (
                                                    <Chip key={p} label={p} size="small" sx={{ height: 22, fontSize: '0.72rem' }} />
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography sx={{ fontSize: '0.9rem', color: 'text.disabled' }}>
                                                {t('help.libraryOnly', 'Library only')}
                                            </Typography>
                                        )}
                                    </DetailField>
                                    <DetailField icon={<TranslateIcon />} label={t('help.fieldLanguage', 'Language')}>
                                        {langLabel(selected.language)}
                                    </DetailField>
                                    <DetailField icon={<ScheduleIcon />} label={t('help.fieldDuration', 'Duration')}>
                                        {formatDuration(selected.duration) || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                                    </DetailField>
                                    <DetailField icon={<CheckCircleOutlineIcon />} label={t('help.fieldStatus', 'Status')}>
                                        <Chip
                                            label={selected.status === 'published' ? t('help.published', 'Published') : t('help.draft', 'Draft')}
                                            size="small"
                                            color={selected.status === 'published' ? 'success' : 'default'}
                                            sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
                                        />
                                    </DetailField>
                                    <DetailField icon={<EventAvailableOutlinedIcon />} label={t('help.fieldPublished', 'Published date')}>
                                        {formatDate(selected.publishedAt)}
                                    </DetailField>
                                    <DetailField icon={<CalendarTodayOutlinedIcon />} label={t('help.fieldCreated', 'Created')}>
                                        {formatDate(selected.createdAt)}
                                    </DetailField>
                                    <DetailField icon={<UpdateIcon />} label={t('help.fieldUpdated', 'Last updated')}>
                                        {formatDate(selected.updatedAt)}
                                    </DetailField>
                                </Box>
                            </Box>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
